import React from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, Check, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { MaskedInput } from '@/components/ui/masked-input'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import type { TimeEstablishment, TimeTrackingSetupConfig } from '@/types/timeTracking'
import { TT_LIMITS, formatEmployerId, onlyDigits, type SetupErrors } from './timeTrackingUtils'

interface Props {
  config: TimeTrackingSetupConfig
  onChange: (config: TimeTrackingSetupConfig) => void
  errors?: SetupErrors | null
  disabled?: boolean
  /** The CPF of who registers is asked here (the wizard); elsewhere the API may take the operator's own record. */
  askResponsible?: boolean
  /** Editing the employer already linked: only its data, no "use another one". */
  newOnly?: boolean
}

const pill = (active: boolean) => cn('inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
  active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')

const FieldError = ({ message }: { message?: string }) => (message ? <p className="mt-1 text-xs text-red-600">{message}</p> : null)

const Field = ({ label, hint, error, children, optional }: { label: string; hint?: string; error?: string; children: React.ReactNode; optional?: string }) => (
  <div className="min-w-0">
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {label}{optional && <span className="ml-1.5 text-xs font-normal text-gray-400">{optional}</span>}
    </label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    <FieldError message={error} />
  </div>
)

/**
 * The establishment step (F-51 criterion 1): the employer the space's punches
 * belong to. A new one (prefilled from the community's company data, as a
 * suggestion) or one the community already has. One component for the
 * wizard step and the space edit (PADROES-UI section 14).
 */
export const EstablishmentFields: React.FC<Props> = ({ config, onChange, errors, disabled = false, askResponsible = true, newOnly = false }) => {
  const { t } = useTranslation(['timeTracking', 'common'])
  const [establishments, setEstablishments] = React.useState<TimeEstablishment[] | null>(null)
  const [prefilled, setPrefilled] = React.useState(false)
  const configRef = React.useRef(config)
  configRef.current = config
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange

  React.useEffect(() => {
    let alive = true
    void (async () => {
      const [list, prefill] = await Promise.all([
        sdk.timeTracking.listEstablishments().catch(() => [] as TimeEstablishment[]),
        sdk.timeTracking.establishmentPrefill().catch(() => null)
      ])
      if (!alive) return
      setEstablishments(list)
      const current = configRef.current
      /* Only an untouched step is filled in: anything the person typed (or the space already has) stays. */
      if (current.linkedEstablishmentId || onlyDigits(current.employerId) || current.legalName || current.workplace) return
      if (list.length > 0 && current.mode === 'new' && !current.establishmentId) {
        onChangeRef.current({ ...current, mode: 'existing', establishmentId: list[0].id, ...suggestion(prefill) })
        if (prefill?.available) setPrefilled(true)
        return
      }
      if (prefill?.available) {
        onChangeRef.current({ ...current, ...suggestion(prefill) })
        setPrefilled(true)
      }
    })()
    return () => { alive = false }
  }, [])

  const set = <K extends keyof TimeTrackingSetupConfig>(key: K, value: TimeTrackingSetupConfig[K]) => onChange({ ...config, [key]: value })
  const hasExisting = !newOnly && (establishments?.length ?? 0) > 0

  if (establishments === null) {
    return <div className="flex items-center gap-2 py-6 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />{t('common:loading')}</div>
  }

  return (
    <div className="min-w-0 space-y-4" data-testid="tt-establishment-fields">
      {hasExisting && (
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <button type="button" disabled={disabled} className={pill(config.mode === 'existing')} onClick={() => set('mode', 'existing')} data-testid="tt-establishment-mode-existing">
            {t('establishment.useExisting')}
          </button>
          <button type="button" disabled={disabled} className={pill(config.mode === 'new')} onClick={() => set('mode', 'new')} data-testid="tt-establishment-mode-new">
            {t('establishment.createNew')}
          </button>
        </div>
      )}

      {config.mode === 'existing' && hasExisting ? (
        <div className="space-y-2" role="radiogroup">
          {establishments.map((e) => {
            const selected = config.establishmentId === e.id
            return (
              <button key={e.id} type="button" role="radio" aria-checked={selected} disabled={disabled}
                onClick={() => set('establishmentId', e.id)}
                className={cn('flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors', selected ? 'border-primary bg-primary-50' : 'border-gray-200 hover:bg-gray-50')}
                data-testid="tt-establishment-option">
                <Building2 className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-gray-400')} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900">{e.legalName}</span>
                  <span className="block text-xs text-gray-500">{e.employerIdType} {formatEmployerId(e.employerIdType, e.employerId)} · {e.workplace}</span>
                </span>
                {selected && <Check className="h-4 w-4 flex-shrink-0 text-primary" />}
              </button>
            )
          })}
          <FieldError message={errors?.establishmentId} />
          <p className="text-xs text-gray-500">{t('establishment.sharedHint')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {prefilled && <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-800">{t('establishment.prefilled')}</p>}
          <Field label={t('establishment.employerIdType')}>
            <div className="flex gap-2">
              {(['CNPJ', 'CPF'] as const).map((type) => (
                <button key={type} type="button" disabled={disabled} className={pill(config.employerIdType === type)}
                  onClick={() => onChange({ ...config, employerIdType: type, employerId: '' })} data-testid={`tt-employer-type-${type}`}>
                  {type}
                </button>
              ))}
            </div>
          </Field>
          <Field label={config.employerIdType} error={errors?.employerId}>
            <MaskedInput mask={config.employerIdType === 'CPF' ? 'cpf' : 'cnpj'} inputMode="numeric" disabled={disabled}
              value={config.employerId} onValueChange={(v) => set('employerId', v)} data-testid="tt-employer-id" />
          </Field>
          <Field label={t('establishment.legalName')} error={errors?.legalName}>
            <Input value={config.legalName} maxLength={TT_LIMITS.legalName} disabled={disabled} onChange={(e) => set('legalName', e.target.value)} data-testid="tt-legal-name" />
          </Field>
          <Field label={t('establishment.workplace')} hint={t('establishment.workplaceHint')} error={errors?.workplace}>
            <Input value={config.workplace} maxLength={TT_LIMITS.workplace} disabled={disabled} onChange={(e) => set('workplace', e.target.value)} data-testid="tt-workplace" />
          </Field>
          <div className="grid gap-3 sm:grid-cols-3">
            {(['caepf', 'cno', 'cei'] as const).map((key) => (
              <Field key={key} label={t(`establishment.${key}`)} optional={t('establishment.optional')} error={errors?.[key]}>
                <Input value={config[key]} inputMode="numeric" maxLength={TT_LIMITS.code + 6} disabled={disabled} onChange={(e) => set(key, e.target.value)} />
              </Field>
            ))}
          </div>
          {askResponsible && (
            <Field label={t('establishment.responsibleCpf')} hint={t('establishment.responsibleHint')} error={errors?.responsibleCpf}>
              <MaskedInput mask="cpf" inputMode="numeric" disabled={disabled} value={config.responsibleCpf} onValueChange={(v) => set('responsibleCpf', v)} data-testid="tt-responsible-cpf" />
            </Field>
          )}
        </div>
      )}
      {errors?.form && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{errors.form}</p>}
    </div>
  )
}

const suggestion = (prefill: { available: boolean; employerIdType: 'CNPJ' | 'CPF' | null; employerId: string | null; legalName: string | null; workplace: string | null } | null): Partial<TimeTrackingSetupConfig> =>
  prefill?.available
    ? {
        ...(prefill.employerIdType ? { employerIdType: prefill.employerIdType } : {}),
        employerId: prefill.employerId || '',
        legalName: (prefill.legalName || '').slice(0, TT_LIMITS.legalName),
        workplace: (prefill.workplace || '').slice(0, TT_LIMITS.workplace)
      }
    : {}
